import { NOTIFICATION_TYPE_SYSTEM_ALERT, NotificationRecord } from './NotificationTypes';

const AVATAR_COLORS = ['#7E3AF2', '#0E7C5A', '#C81E5B', '#1C64F2', '#B54708', '#5521B5'];

const ACTION_LABELS: Record<string, string> = {
  board_shared: 'shared a board with you',
  artifact_shared: 'shared an artifact with you',
};

const SYSTEM_SENDER = 'System';

/**
 * Where a notification takes you when clicked. Types with no destination
 * (system_alert) map to nothing and are click-to-dismiss only.
 *
 * These paths come from useRoutes: '/artifacts' -> ArtifactsComponent,
 * '/studio' -> PinboardComponent. Both are gated on `show: !isWorkSpaceOwner`,
 * so for a workspace owner the route isn't registered and the router's
 * path="*" catch-all sends you to '/' instead of a blank screen.
 */
const NOTIFICATION_ROUTES: Record<string, string> = {
  artifact_shared: '/artifacts',
  board_shared: '/studio',
};

/** Query params the Artifacts/Pinboard screens read to open the exact item. */
export const DEEP_LINK_PARAMS = {
  sharedArtifactId: 'sharedArtifactId',
  readoutId: 'readoutId',
  boardId: 'boardId',
};

const toQueryString = (params: Record<string, number | null | undefined>) => {
  const search = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined) search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

/**
 * Route plus the id of the shared item, so the screen opens it directly rather
 * than its list. Missing ids just drop out of the query -- the list still shows.
 */
export const getNotificationRoute = (item: NotificationRecord) => {
  const base = NOTIFICATION_ROUTES[item.notification_type];
  if (!base) return '';

  const payload = item.payload || {};
  if (item.notification_type === 'artifact_shared') {
    return base + toQueryString({
      [DEEP_LINK_PARAMS.sharedArtifactId]: payload.shared_artifact_id,
      [DEEP_LINK_PARAMS.readoutId]: payload.readout_id ?? payload.artifact_id,
    });
  }
  return base + toQueryString({
    [DEEP_LINK_PARAMS.boardId]: payload.board_id ?? payload.shared_pinboard_id ?? payload.pinboard_id,
  });
};

/**
 * Display name for the row. The list endpoint returns no recipient_user_email,
 * and server-generated types (system_alert) have no sender at all, so fall
 * through username -> email -> "System".
 */
export const getSenderName = (item: NotificationRecord) =>
  item.sender_username || item.sender_user_email || SYSTEM_SENDER;

export const getInitials = (name: string) => {
  const initials = (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
  return initials || '?';
};

/** Same sender always gets the same colour. */
export const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let index = 0; index < (name || '').length; index += 1) {
    hash += name.charCodeAt(index);
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

export const getActionLabel = (type: string) =>
  ACTION_LABELS[type] || 'sent you a notification';

/** system_alert has no sender to name, so it gets its own heading. */
export const getHeading = (item: NotificationRecord) => {
  if (item.notification_type === NOTIFICATION_TYPE_SYSTEM_ALERT) return 'System alert';
  return `${getSenderName(item)} ${getActionLabel(item.notification_type)}`;
};

export const getResourceName = (item: NotificationRecord) =>
  item.payload?.artifact_name || item.payload?.pinboard_name || item.payload?.title || '';

/**
 * The dark part of the meta line. Ordered most- to least-specific; every field
 * is nullable server-side, so `||` skips nulls as well as empty strings.
 */
export const getPrimaryText = (item: NotificationRecord) =>
  item.payload?.description ||
  item.payload?.message ||
  getResourceName(item) ||
  '';

export const getResourceType = (item: NotificationRecord) => {
  if (item.payload?.artifact_type) return item.payload.artifact_type;
  return item.notification_type === 'board_shared' ? 'Pinboard' : '';
};

/**
 * The API sends UTC with no zone designator and a space separator, e.g.
 * "2026-09-18 06:15:12.578463". Browsers parse that form as LOCAL time, which
 * skews every timestamp by the viewer's offset (5h30m in IST). Normalise to
 * an explicit-UTC ISO string so it converts to local time correctly.
 */
export const parseServerDate = (value: string) => {
  if (!value) return new Date(NaN);

  let normalized = value.trim().replace(' ', 'T');

  // JS parses milliseconds at most; microseconds make some engines return NaN.
  normalized = normalized.replace(/(\.\d{3})\d+/, '$1');

  // Only stamp UTC when the server didn't specify a zone -- never override one.
  const hasTime = normalized.indexOf('T') !== -1;
  const hasZone = /(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(normalized);
  if (hasTime && !hasZone) normalized += 'Z';

  return new Date(normalized);
};

export const getRelativeTime = (isoDate: string) => {
  const created = parseServerDate(isoDate);
  if (isNaN(created.getTime())) return '';

  const diffMinutes = Math.floor((Date.now() - created.getTime()) / 60000);
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return created.toLocaleDateString();
};
